import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { FilterLinesIcon, Stars02Icon, Users01Icon, XCloseIcon } from '@untitledui-icons/react/line'
import { Button } from '@/components/base/buttons'
import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
  SidePanelFooter,
} from '@/components/application/side-panel'
import { FormSelect } from '@/components/forms/FormSelect'
import { FormSwitch } from '@/components/forms/FormSwitch'
import { AsyncMultiSelect, type SelectOption } from '@/components/forms/AsyncMultiSelect'
import { useForm, FormProvider } from 'react-hook-form'
import { useAssistantsByIds } from '@/hooks/api/useAssistants'
import { useUsersByIds } from '@/hooks/api/useUsers'
import type { NotesListFilters } from '@/types/notes'
import type { NoteStatus, PriorityLevel } from '@/types/conversation'

interface NoteFiltersProps {
  filters: NotesListFilters
  onFiltersChange: (filters: NotesListFilters) => void
  /** Called before clearing filters - use to clear persisted storage */
  onClear?: () => void
}

interface FilterFormValues {
  assistant_id: number[]
  status: string
  assigned_to: number[]
  is_private: boolean
  priority: string
}

export function NoteFilters({ filters, onFiltersChange, onClear }: NoteFiltersProps) {
  const { t } = useTranslation()

  const statusOptions = [
    { value: '', label: t('resources.notes.filters.all_statuses') },
    { value: 'open', label: t('resources.conversation_notes.status.open') },
    { value: 'closed', label: t('resources.conversation_notes.status.closed') },
    { value: 'none', label: t('resources.notes.filters.none') },
  ]

  const priorityOptions = [
    { value: '', label: t('resources.notes.filters.all_priorities') },
    { value: 'low', label: t('resources.conversation_notes.priority.low') },
    { value: 'medium', label: t('resources.conversation_notes.priority.medium') },
    { value: 'high', label: t('resources.conversation_notes.priority.high') },
    { value: 'critical', label: t('resources.conversation_notes.priority.critical') },
  ]

  const [isOpen, setIsOpen] = useState(false)

  // Selected items state
  const [selectedAssistants, setSelectedAssistants] = useState<SelectOption[]>([])
  const [selectedUsers, setSelectedUsers] = useState<SelectOption[]>([])

  const methods = useForm<FilterFormValues>({
    defaultValues: {
      assistant_id: filters.assistant_id || [],
      status: filters.status || '',
      assigned_to: filters.assigned_to || [],
      is_private: filters.is_private || false,
      priority: filters.priority || '',
    },
  })

  // Fetch selected assistants when filters change
  const { data: existingAssistants = [] } = useAssistantsByIds(
    filters.assistant_id || [],
    (filters.assistant_id?.length || 0) > 0
  )

  // Create stable keys for comparison to avoid infinite loops
  const existingAssistantIds = existingAssistants.map((a) => a.id).join(',')
  const filterAssistantIds = (filters.assistant_id || []).join(',')

  // Initialize selected assistants from fetched data
  useEffect(() => {
    if (existingAssistants.length > 0) {
      setSelectedAssistants(existingAssistants.map((a) => ({ id: a.id, name: a.name })))
    } else if (!filters.assistant_id?.length) {
      setSelectedAssistants([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingAssistantIds, filterAssistantIds])

  // Fetch selected users reactively when filter IDs exist
  const { data: existingUsers = [] } = useUsersByIds(
    filters.assigned_to || [],
    (filters.assigned_to?.length || 0) > 0
  )

  // Create stable keys for users comparison
  const existingUserIds = existingUsers.map((u) => u.id).join(',')
  const filterUserIds = (filters.assigned_to || []).join(',')

  // Initialize selected users from fetched data
  useEffect(() => {
    if (existingUsers.length > 0) {
      setSelectedUsers(existingUsers.map((u) => ({ id: u.id, name: u.name || `${u.first_name} ${u.last_name}` })))
    } else if (!filters.assigned_to?.length) {
      setSelectedUsers([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingUserIds, filterUserIds])

  const handleApply = useCallback(
    (values: FilterFormValues) => {
      const newFilters: NotesListFilters = {}

      if (selectedAssistants.length > 0) {
        newFilters.assistant_id = selectedAssistants.map((a) => a.id)
      }
      if (values.status) {
        newFilters.status = values.status as NoteStatus
      }
      if (selectedUsers.length > 0) {
        newFilters.assigned_to = selectedUsers.map((u) => u.id)
      }
      if (values.is_private) {
        newFilters.is_private = values.is_private
      }
      if (values.priority) {
        newFilters.priority = values.priority as PriorityLevel
      }

      onFiltersChange(newFilters)
      setIsOpen(false)
    },
    [onFiltersChange, selectedAssistants, selectedUsers]
  )

  const handleClear = useCallback(() => {
    methods.reset({
      assistant_id: [],
      status: '',
      assigned_to: [],
      is_private: false,
      priority: '',
    })
    setSelectedAssistants([])
    setSelectedUsers([])
    // Clear persisted storage first to prevent restoration
    onClear?.()
    onFiltersChange({})
    setIsOpen(false)
  }, [methods, onFiltersChange, onClear])

  // Quick clear without opening panel
  const handleQuickClear = useCallback(() => {
    methods.reset({
      assistant_id: [],
      status: '',
      assigned_to: [],
      is_private: false,
      priority: '',
    })
    setSelectedAssistants([])
    setSelectedUsers([])
    // Clear persisted storage first to prevent restoration
    onClear?.()
    onFiltersChange({})
  }, [methods, onFiltersChange, onClear])

  const activeFilterCount = Object.keys(filters).filter((key) => {
    const value = filters[key as keyof NotesListFilters]
    return value !== undefined && value !== '' && value !== false && (!Array.isArray(value) || value.length > 0)
  }).length

  return (
    <>
      <div className="inline-flex items-center overflow-hidden rounded-lg border border-gray-300 bg-white shadow-xs dark:border-gray-600 dark:bg-gray-800">
        <button
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          <FilterLinesIcon className="h-4 w-4" />
          {t('resources.generic.open_filters')}
          {activeFilterCount > 0 && (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-500 px-1.5 text-xs font-medium text-white">
              {activeFilterCount}
            </span>
          )}
        </button>
        {activeFilterCount > 0 && (
          <>
            <div className="h-5 w-px bg-gray-300 dark:bg-gray-600" />
            <button
              onClick={handleQuickClear}
              className="flex items-center justify-center px-2 py-1.5 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
              title={t('resources.generic.clear_all')}
            >
              <XCloseIcon className="h-4 w-4" />
            </button>
          </>
        )}
      </div>

      <SidePanel
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        onClose={() => setIsOpen(false)}
        side="right"
        size="md"
        variant="filter"
      >
        <SidePanelContent>
          <SidePanelHeader title={t('resources.generic.filters')} onClose={() => setIsOpen(false)} />
          <SidePanelBody>
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(handleApply)} className="space-y-6">
                {/* Assistant filter */}
                <AsyncMultiSelect
                  label={t('resources.notes.filters.assistants')}
                  placeholder={t('resources.notes.filters.select_assistants')}
                  searchPlaceholder={t('resources.generic.search')}
                  searchEndpoint="/assistants"
                  value={selectedAssistants}
                  onChange={setSelectedAssistants}
                  emptyMessage={t('resources.generic.no_results')}
                  icon={Stars02Icon}
                />

                {/* Status filter */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('resources.notes.filters.status')}
                  </label>
                  <FormSelect name="status" options={statusOptions} />
                </div>

                {/* Assigned to filter */}
                <AsyncMultiSelect
                  label={t('resources.notes.filters.assigned_to')}
                  placeholder={t('resources.notes.filters.select_users')}
                  searchPlaceholder={t('resources.generic.search')}
                  searchEndpoint="/users"
                  value={selectedUsers}
                  onChange={setSelectedUsers}
                  emptyMessage={t('resources.generic.no_results')}
                  icon={Users01Icon}
                  transformResponse={(item) => ({
                    id: item.id as number,
                    name: `${item.first_name} ${item.last_name}`,
                  })}
                  buildFilter={(search) => ({
                    _or: [
                      { first_name: `%${search}%` },
                      { last_name: `%${search}%` },
                    ],
                  })}
                  sortField="first_name"
                />

                {/* Private filter */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('resources.notes.filters.private')}
                  </label>
                  <FormSwitch name="is_private" label={t('resources.notes.filters.is_private')} />
                </div>

                {/* Priority filter */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('resources.notes.filters.priority')}
                  </label>
                  <FormSelect name="priority" options={priorityOptions} />
                </div>
              </form>
            </FormProvider>
          </SidePanelBody>
          <SidePanelFooter>
            <Button color="secondary" onPress={handleClear}>
              {t('resources.generic.clear_all')}
            </Button>
            <Button color="primary" onPress={methods.handleSubmit(handleApply)}>
              {t('resources.generic.apply_filters')}
            </Button>
          </SidePanelFooter>
        </SidePanelContent>
      </SidePanel>
    </>
  )
}

export default NoteFilters
